import {
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useMemo,
  useState,
} from "react";
import { format, startOfDay } from "date-fns";
import clsx from "clsx";
import {
  ArrowLeft,
  CalendarDays,
  ClipboardList,
  Clock,
  Dumbbell,
  Search,
  Target,
  Zap,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { InputDatePicker } from "@/components/ui/input-date-picker";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import LoadingSpinner from "@/components/LoadingSpinner";
import { useWeekStartsOn } from "@/lib/dates/useWeekStartsOn";
import { useOrg } from "@/lib/states/session/hooks";
import { trpc, type RouterOutputs } from "@/lib/trpc/client";
import {
  DistanceUnit,
  formatDistance,
  formatTimeSeconds,
} from "@/lib/utils/exerciseTracking";

type TemplatePlanSummary = {
  id: string;
  name: string;
  org_id: string | null;
  is_template: boolean;
  scheduling_mode?: "fixed" | "dated" | null;
  workoutCount?: number;
};

type TrainingTemplateLinkDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  templatePlans: TemplatePlanSummary[];
  linkedPlanIds: Set<string>;
  onLinkTemplate: (
    template: TemplatePlanSummary,
    startDate?: string,
  ) => Promise<boolean>;
};

export function TrainingTemplateLinkDialog({
  open,
  onOpenChange,
  templatePlans,
  linkedPlanIds,
  onLinkTemplate,
}: TrainingTemplateLinkDialogProps) {
  const weekStartsOn = useWeekStartsOn();
  const [searchTerm, setSearchTerm] = useState("");
  const [showDetails, setShowDetails] = useState(false);
  const [selectedId, setSelectedId] = useState<string>();
  const [startDate, setStartDate] = useState(
    format(startOfDay(new Date()), "yyyy-MM-dd"),
  );
  const [linking, setLinking] = useState(false);

  const filteredTemplates = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return templatePlans;
    return templatePlans.filter((template) =>
      template.name.toLowerCase().includes(query),
    );
  }, [searchTerm, templatePlans]);

  const selectedTemplate = useMemo(
    () => templatePlans.find((template) => template.id === selectedId),
    [templatePlans, selectedId],
  );

  const { data: planDetails, isLoading: planLoading, isError: planError } =
    trpc.training.getPlan.useQuery(
      { planId: selectedTemplate?.id ?? "" },
      { enabled: Boolean(open && selectedTemplate?.id) },
    );

  const today = () => format(startOfDay(new Date()), "yyyy-MM-dd");

  useEffect(() => {
    if (!open) {
      setSearchTerm("");
      setShowDetails(false);
      setSelectedId(undefined);
      setStartDate(format(startOfDay(new Date()), "yyyy-MM-dd"));
      setLinking(false);
      return;
    }
    if (filteredTemplates.length === 0) return;
    const stillVisible = selectedId
      ? filteredTemplates.some((template) => template.id === selectedId)
      : false;
    if (!stillVisible) {
      setSelectedId(filteredTemplates[0].id);
    }
  }, [open, filteredTemplates, selectedId]);

  const handleSelect = (template: TemplatePlanSummary) => {
    setSelectedId(template.id);
    setStartDate(today());
    if (window.innerWidth < 768) {
      setShowDetails(true);
    }
  };

  const handleLink = async () => {
    if (!selectedTemplate || linkedPlanIds.has(selectedTemplate.id)) return;
    setLinking(true);
    const isCalendarTemplate = selectedTemplate.scheduling_mode === "dated";
    try {
      const success = await onLinkTemplate(
        selectedTemplate,
        isCalendarTemplate ? startDate : undefined,
      );
      if (success) {
        onOpenChange(false);
      }
    } finally {
      setLinking(false);
    }
  };

  const isLinked = selectedTemplate
    ? linkedPlanIds.has(selectedTemplate.id)
    : false;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      analyticsName="training_template_link_dialog"
    >
      <DialogContent className="max-w-6xl w-[95vw] h-[90vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 py-4 border-b">
          <DialogTitle className="text-2xl font-bold text-foreground">
            Choose a training template
          </DialogTitle>
          <DialogDescription>
            Link a template plan to this client. Any edits will create a new
            plan on save.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col md:flex-row h-full overflow-hidden">
          <div
            className={clsx(
              "flex min-h-0 flex-col border-r bg-muted/30",
              showDetails ? "hidden md:flex md:w-80" : "w-full md:w-80",
            )}
          >
            <div className="p-4 border-b">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search templates..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border rounded-lg bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>
            </div>

            <ScrollArea className="flex-1 min-h-0">
              <div className="p-2 space-y-2">
                {filteredTemplates.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Dumbbell className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No training templates found</p>
                  </div>
                ) : (
                  filteredTemplates.map((template) => {
                    const alreadyLinked = linkedPlanIds.has(template.id);
                    const workoutCount = template.workoutCount ?? 0;
                    const isCalendar = template.scheduling_mode === "dated";
                    return (
                      <Card
                        key={template.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => handleSelect(template)}
                        onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            handleSelect(template);
                          }
                        }}
                        className={clsx(
                          "p-4 cursor-pointer transition-all duration-200 hover:shadow-md border-2 select-none",
                          template.id === selectedTemplate?.id
                            ? "border-primary bg-primary/5 shadow-md"
                            : "border-border hover:border-primary/30",
                        )}
                      >
                        <div className="space-y-2">
                          <div className="flex items-start justify-between gap-2">
                            <h3 className="font-semibold text-foreground line-clamp-1">
                              {template.name}
                            </h3>
                            {alreadyLinked ? (
                              <span className="shrink-0 text-[11px] font-medium text-muted-foreground">
                                Linked
                              </span>
                            ) : null}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Target className="h-3 w-3" />
                            <span>
                              {workoutCount}{" "}
                              {workoutCount === 1 ? "workout" : "workouts"}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            {isCalendar ? (
                              <CalendarDays className="h-3 w-3" />
                            ) : (
                              <ClipboardList className="h-3 w-3" />
                            )}
                            <span>
                              {isCalendar ? "Calendar template" : "Fixed template"}
                            </span>
                          </div>
                        </div>
                      </Card>
                    );
                  })
                )}
              </div>
            </ScrollArea>
          </div>

          <div className="hidden md:flex flex-1 flex-col min-h-0 overflow-hidden">
            {selectedTemplate ? (
              <TrainingPlanPreview
                template={selectedTemplate}
                planDetails={planDetails}
                isLoading={planLoading}
                isError={planError}
              />
            ) : (
              <EmptyPreview />
            )}
          </div>

          <div
            className={clsx(
              "md:hidden flex-1 flex flex-col min-h-0 overflow-hidden",
              showDetails ? "block" : "hidden",
            )}
          >
            {selectedTemplate ? (
              <TrainingPlanPreview
                template={selectedTemplate}
                planDetails={planDetails}
                isLoading={planLoading}
                isError={planError}
                onBack={() => setShowDetails(false)}
              />
            ) : null}
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t bg-muted/20 flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          {selectedTemplate?.scheduling_mode === "dated" ? (
            <div className="w-full sm:max-w-xs">
              <InputDatePicker
                label="Start day"
                value={startDate}
                onChange={setStartDate}
                weekStartsOn={weekStartsOn}
              />
            </div>
          ) : (
            <div className="hidden sm:block" />
          )}
          <div className="flex gap-3 w-full sm:w-auto">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1 sm:flex-none"
            >
              Cancel
            </Button>
            <Button
              disabled={!selectedTemplate || isLinked || linking}
              onClick={() => void handleLink()}
              className="flex-1 sm:flex-none"
            >
              {isLinked ? "Linked" : "Link template"}
              {linking ? <LoadingSpinner className="ml-2" /> : null}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const EmptyPreview = () => (
  <div className="flex-1 flex items-center justify-center">
    <div className="text-center text-muted-foreground">
      <Dumbbell className="h-16 w-16 mx-auto mb-4 opacity-50" />
      <p className="text-lg">Select a training plan to view details</p>
    </div>
  </div>
);

type PlanDetails = RouterOutputs["training"]["getPlan"];

type SetTarget = {
  startRange?: number;
  endRange?: number;
  targetTimeSeconds?: number;
  targetDistanceMeters?: number;
};

const TrainingPlanPreview = ({
  template,
  planDetails,
  isLoading,
  isError,
  onBack,
}: {
  template: TemplatePlanSummary;
  planDetails?: PlanDetails;
  isLoading: boolean;
  isError: boolean;
  onBack?: () => void;
}) => {
  const isCalendar = template.scheduling_mode === "dated";
  const workouts = planDetails?.workouts ?? [];

  return (
    <>
      <div className="flex-shrink-0 p-4 md:p-6 border-b bg-gradient-to-r from-background to-muted/20">
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            {onBack ? (
              <button
                onClick={onBack}
                className="p-2 hover:bg-muted/50 rounded-lg transition-colors"
              >
                <ArrowLeft className="h-5 w-5 text-foreground" />
              </button>
            ) : null}
            <h1 className="text-xl md:text-2xl font-bold text-foreground">
              {template.name}
            </h1>
          </div>
          {planDetails?.description ? (
            <p className="text-muted-foreground leading-relaxed line-clamp-3">
              {planDetails.description}
            </p>
          ) : null}
          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Target className="h-4 w-4" />
              <span>
                {template.workoutCount ?? workouts.length}{" "}
                {(template.workoutCount ?? workouts.length) === 1
                  ? "workout"
                  : "workouts"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {isCalendar ? (
                <CalendarDays className="h-4 w-4" />
              ) : (
                <ClipboardList className="h-4 w-4" />
              )}
              <span>{isCalendar ? "Calendar template" : "Fixed template"}</span>
            </div>
          </div>
        </div>
      </div>

      <ScrollArea className="flex-1 min-h-0">
        <div className="p-4 md:p-6 space-y-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <LoadingSpinner />
            </div>
          ) : isError ? (
            <p className="text-sm text-muted-foreground">
              Couldn&apos;t load this template preview.
            </p>
          ) : workouts.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              This template has no workouts yet.
            </p>
          ) : (
            workouts.map((workout, workoutIndex) => (
              <WorkoutPreview
                key={workout.id}
                workout={workout}
                index={workoutIndex}
                isLast={workoutIndex === workouts.length - 1}
              />
            ))
          )}
        </div>
      </ScrollArea>
    </>
  );
};

const WorkoutPreview = ({
  workout,
  index,
  isLast,
}: {
  workout: PlanDetails["workouts"][number];
  index: number;
  isLast: boolean;
}) => {
  const org = useOrg();
  const distanceUnitLabel: DistanceUnit =
    org?.distanceUnitDefault === "mi" ? "mi" : "km";
  const exercises = workout.exercises ?? [];

  const getSetTargetLabel = (trackingType: string | null, set: SetTarget) => {
    if (trackingType?.startsWith("distance")) {
      const distanceLabel = formatDistance(
        set.targetDistanceMeters,
        distanceUnitLabel,
        trackingType,
      );
      const timeLabel = set.targetTimeSeconds
        ? ` in ${formatTimeSeconds(set.targetTimeSeconds)}`
        : "";
      return `${distanceLabel}${timeLabel}`.trim();
    }
    if (trackingType === "timed") {
      return formatTimeSeconds(set.targetTimeSeconds);
    }
    return `${set.startRange} - ${set.endRange} reps`;
  };

  return (
    <div className="space-y-4 select-none">
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-sm font-semibold text-primary">
          {index + 1}
        </div>
        <div className="flex-1 min-w-0 space-y-4">
          <div>
            <h3 className="text-lg font-semibold text-foreground">
              {workout.name}
            </h3>
            {workout.description ? (
              <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                {workout.description}
              </p>
            ) : null}
            <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
              <Target className="h-3 w-3" />
              <span>
                {exercises.length}{" "}
                {exercises.length === 1 ? "exercise" : "exercises"}
              </span>
            </div>
          </div>

          {exercises.map((exercise) => (
            <div key={exercise.workout_exercise_id ?? exercise.id} className="space-y-3">
              <h4 className="font-medium text-foreground">{exercise.name}</h4>
              <div className="flex flex-wrap gap-3">
                {exercise.tempo ? (
                  <BadgeWithTitle
                    title="Tempo"
                    value={exercise.tempo}
                    icon={<Clock className="h-3 w-3" />}
                  />
                ) : null}
                {exercise.rest_time !== null &&
                exercise.rest_time !== undefined ? (
                  <BadgeWithTitle
                    title="Rest"
                    value={`${exercise.rest_time}s`}
                    icon={<Zap className="h-3 w-3" />}
                  />
                ) : null}
                {exercise.rir !== null && exercise.rir !== undefined ? (
                  <BadgeWithTitle
                    title="RIR"
                    value={exercise.rir.toString()}
                    icon={<Target className="h-3 w-3" />}
                  />
                ) : null}
              </div>
              {exercise.sets && exercise.sets.length > 0 ? (
                <div className="space-y-2">
                  <h5 className="text-sm font-medium text-muted-foreground">
                    Sets:
                  </h5>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                    {exercise.sets.map((set, setIndex: number) => (
                      <div
                        key={setIndex}
                        className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg"
                      >
                        <span className="w-6 h-6 bg-primary/20 rounded-full flex items-center justify-center text-xs font-semibold text-primary">
                          {setIndex + 1}
                        </span>
                        <span className="text-sm text-foreground">
                          {getSetTargetLabel(
                            exercise.tracking_type,
                            set as SetTarget,
                          )}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          ))}
        </div>
      </div>
      {!isLast ? <Separator className="my-6" /> : null}
    </div>
  );
};

const BadgeWithTitle = ({
  title,
  value,
  icon,
}: {
  title: string;
  value: string;
  icon?: ReactNode;
}) => (
  <div className="flex items-center gap-2 px-3 py-2 bg-muted/50 rounded-lg border border-border/50">
    {icon}
    <div className="flex flex-col">
      <span className="text-xs text-muted-foreground font-medium">{title}</span>
      <span className="text-sm font-semibold text-foreground">{value}</span>
    </div>
  </div>
);
